import React, { useCallback, useMemo } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  List,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { size } from "lodash"
import { useParams, useSearchParams } from "react-router-dom"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"

import { getIcon } from "utils/icon"

import { Q_VIEW_MODE, SHOP_PROMOTION_VIEW_MODE } from "constant"
import { getDurationDate } from "utils/date"

import { useImageModal } from "hooks/imageModal/useImageModal"
import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"
import { useAppUI } from "providers/ui"
import { useMobileUrl } from "hooks/useMobileUrl"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useTranslation } from "providers/i18n"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { ContentWithAside } from "layouts/kiosk-h"
import { InfoListItem } from "components/ListItem"
import { LocationIcon, PercentIcon } from "components/icons"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { ImageCarouselModal } from "components/Image"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const PromotionDetailKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const params = useParams()
  const { promotionId } = params
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const { promotion, navigateToLocation } = usePromotionDetail(promotionId)
  const { goBack } = useAppUI()
  const { mobileUrl } = useMobileUrl()

  const isShopPromotionViewMode =
    searchParams.get(Q_VIEW_MODE) === SHOP_PROMOTION_VIEW_MODE

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const {
    pageCard,
    pageCardContent,
    emptyDataContainer,
    card,
    infoListItem,
    cardContent,
    cardHeader,
    cardContentPrimaryText,
    cardContentSecondaryText,
    infoContainer,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    buttonContainer,
    button,
    cardContentTextWrapper,
    cardMediaWrapper,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    contentWrapper,
    galleryCarouselModal,
  } = theme.components?.[`VuiDetailCard`]?.styleOverrides || {}
  const { galleryCarouselOption } =
    theme.components[`VuiDetailCard`]?.defaultProps || {}

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )
  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const allImagesUrls = useMemo(
    () =>
      (size(promotion?.allImages) > 0
        ? promotion?.allImages?.map((v) => v?.url)
        : []) as string[],
    [promotion?.allImages]
  )

  usePageSideBarLayout({
    sidebar: {
      open: isShopPromotionViewMode,
    },
  })

  return (
    <>
      <Layout
        aside={
          <PageCard>
            <SidebarMenu AppNavigation={AppNavigation} />
          </PageCard>
        }
      >
        <PageCard sx={pageCard}>
          <PageCardContent sx={pageCardContent}>
            {!promotion ? (
              <Box sx={emptyDataContainer}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("No Current Promotions")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            ) : (
              <Card
                elevation={0}
                sx={card}
                className={!promotion.descriptionText ? "no-description" : ""}
              >
                <Box sx={cardMediaWrapper}>
                  {promotion.allImages && (
                    <Box sx={galleryCarouselWrapper}>
                      <LeftToRightWrapper>
                        <Carousel
                          options={{
                            type: "fade",
                            padding: 0,
                            gap: 8,
                            pagination: true,
                            autoplay: true,
                            perPage: 1,
                            ...galleryCarouselOption,
                          }}
                          sx={galleryCarousel}
                        >
                          {promotion.allImages.map(({ id, url }) => {
                            return (
                              <CarouselItem
                                key={`slide-${id}`}
                                sx={galleryCarouselItem}
                              >
                                <CardMedia
                                  component="img"
                                  sx={galleryCarouselItem}
                                  src={url as string}
                                  onClick={() => handleImage(url)}
                                />
                              </CarouselItem>
                            )
                          })}
                        </Carousel>
                      </LeftToRightWrapper>
                    </Box>
                  )}
                </Box>
                <Box sx={contentWrapper}>
                  <CardHeader title={t(promotion.name)} sx={cardHeader} />
                  {promotion.showInfo && (
                    <List sx={infoContainer}>
                      {!promotion.showAlways && (
                        <InfoListItem
                          sx={infoListItem}
                          icon={calendarIcon}
                          primary={getDurationDate(
                            promotion.startDate,
                            promotion.endDate
                          )}
                          secondary={promotion.openingText}
                          direction="ltr"
                        />
                      )}
                      {promotion.hasLocation && (
                        <InfoListItem
                          sx={infoListItem}
                          icon={locationIcon}
                          primary={
                            <LocationInfo
                              feature={promotion.feature}
                              component="span"
                            />
                          }
                          secondary={t(promotion.boothName)}
                        />
                      )}
                    </List>
                  )}
                  <CardContent
                    sx={cardContent}
                    className="VuiDetailCard-cardContent"
                  >
                    <Box sx={cardContentTextWrapper}>
                      <Typography
                        variant="body1"
                        gutterBottom
                        sx={cardContentPrimaryText}
                      >
                        {t("Promotion Detail")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={cardContentSecondaryText}
                        dangerouslySetInnerHTML={{
                          __html: promotion.descriptionText,
                        }}
                      ></Typography>
                    </Box>
                  </CardContent>
                  {mobileUrl && (
                    <Box sx={qrCodeContainer}>
                      <QRCode sx={qrCode} url={mobileUrl} />
                      <Stack>
                        <Typography
                          variant="subtitle2"
                          component="p"
                          sx={qrcodePrimaryText}
                        >
                          {t("Scan Here")}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="p"
                          sx={qrcodeSecondaryText}
                        >
                          {t("For Mobile")}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  {!isShopPromotionViewMode && (
                    <Box sx={buttonContainer}>
                      <Button
                        variant="outlined"
                        sx={button}
                        onClick={handleClickBack}
                      >
                        {t("Back")}
                      </Button>
                      {promotion?.hasLocation && (
                        <Button
                          variant="contained"
                          sx={button}
                          className="highlight"
                          onClick={navigateToLocation}
                        >
                          {t("View Location")}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Card>
            )}
          </PageCardContent>
        </PageCard>
      </Layout>
      <ImageCarouselModal
        className="VuiDetailCard-galleryCarouselModal--promotion"
        sx={galleryCarouselModal}
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={allImagesUrls}
        onClose={handleClose}
        carouselOptions={{
          rewind: false,
        }}
      />
    </>
  )
}
